import { useInfiniteQuery } from '@tanstack/react-query';

import queryKeys from '@pray/shared/queryKeys';
import api from '@pray/shared/utils/api';

export type AccountStatusRow = {
  artistId: string;
  artistPrivateId: number;
  name: string;
  handle: string | null;
  organizationId: number | null;
  organizationName: string | null;
  hasGivingAccount: boolean;
  payoutInterval: string | null;
  isPayoutsManual: boolean;
  isGivingTurnedOff: boolean;
  destroyed: boolean;
  liveRecurringDonations: number;
  deactivationLevel: number | null;
  deactivationStatus: string | null;
};

type GetAccountStatusListParams = {
  query?: string;
  limit?: number;
  offset?: number;
};

export async function getAccountStatusList({
  query = '',
  limit = 50,
  offset = 0,
}: GetAccountStatusListParams = {}): Promise<AccountStatusRow[]> {
  const params = new URLSearchParams();
  if (query) params.set('query', query);
  params.set('limit', String(limit));
  params.set('offset', String(offset));

  const url = `/web/studio/admin/account-status?${params.toString()}`;
  const response = await api.executeGetRequest(url);

  // List envelope: { object: 'list', data_count, data: AccountStatusRow[] }
  return response.data.data;
}

const PAGE_SIZE = 50;

export function useAccountStatusList(params: { query?: string } = {}) {
  const query = useInfiniteQuery({
    queryKey: queryKeys.accountStatusList(params.query ?? ''),
    queryFn: ({ pageParam = 0 }) =>
      getAccountStatusList({ query: params.query, limit: PAGE_SIZE, offset: pageParam as number }),
    initialPageParam: 0,
    // Offset paging: a full page means there may be more; a short page is the end.
    getNextPageParam: (lastPage, allPages) => (lastPage.length === PAGE_SIZE ? allPages.length * PAGE_SIZE : undefined),
    // The list reflects live platform state, so a stale cache would show an operator a status
    // that has already changed underneath them.
    staleTime: 0,
  });

  return {
    ...query,
    data: query.data?.pages.flat() ?? [],
  };
}
